import { useEffect, useMemo, useState } from "react";
import type { PlatformClient } from "../../platform/supabase";
import { CenterPersonalExperienceReview } from "./CenterPersonalExperienceReview";

type EvidenceItem={
  evidenceId:string;
  learnerId:string;
  displayName:string;
  gradeLevel?:string|null;
  classLabel?:string|null;
  title:string;
  evidenceType:string;
  status:string;
  version:number;
  currentRevision:number;
  narrative?:string|null;
  observedAt?:string|null;
  createdAt:string;
  updatedAt:string;
  verifiedAt?:string|null;
  source:{
    type:string;
    participationId?:string;
    activityTitle?:string;
    programTitle?:string;
    participationStatus?:string;
    assessmentResultId?:string;
    assessmentTargetType?:string;
    rawScore?:number|null;
    normalizedScore?:number|null;
  };
  evaluationLinks:Array<{
    evaluationResultId:string;
    definitionName:string;
    status:string;
    numericScore?:number|null;
    normalizedScore?:number|null;
  }>;
};

type Workbench={
  counts:{draft:number;submitted:number;rejected:number;verified:number};
  items:EvidenceItem[];
};

type StatusFilter="ALL"|"SUBMITTED"|"REJECTED"|"DRAFT"|"VERIFIED";

function statusLabel(value:string){
  const map:Record<string,string>={
    DRAFT:"작성 중",
    SUBMITTED:"검토 대기",
    VERIFIED:"검증 완료",
    REJECTED:"보완 필요"
  };
  return map[value] || value;
}

function formatDate(value?:string|null){
  if(!value)return "-";
  return new Date(value).toLocaleString("ko-KR",{
    year:"numeric",month:"2-digit",day:"2-digit",hour:"2-digit",minute:"2-digit"
  });
}

export function CenterEvidenceWorkbench({
  client,
  schoolId
}:{
  client:PlatformClient;
  schoolId:string;
}){
  const [data,setData]=useState<Workbench|null>(null);
  const [filter,setFilter]=useState<StatusFilter>("SUBMITTED");
  const [selectedId,setSelectedId]=useState("");
  const [reason,setReason]=useState("");
  const [revision,setRevision]=useState("");
  const [busy,setBusy]=useState("");
  const [error,setError]=useState("");
  const [refresh,setRefresh]=useState(0);

  useEffect(()=>{
    let active=true;
    setError("");
    client.rpc("center_evidence_workbench",{p_school_id:schoolId}).then(({data,error})=>{
      if(!active)return;
      if(error){setError(error.message);return;}
      const next=data as Workbench;
      setData(next);
      setSelectedId((current)=>
        next.items.some((item)=>item.evidenceId===current)
          ? current
          : next.items.find((item)=>item.status==="SUBMITTED")?.evidenceId
            || next.items[0]?.evidenceId
            || ""
      );
    });
    return()=>{active=false;};
  },[client,schoolId,refresh]);

  const filtered=useMemo(
    ()=>data?.items.filter((item)=>filter==="ALL" || item.status===filter) || [],
    [data,filter]
  );

  useEffect(()=>{
    if(!filtered.length){
      setSelectedId("");
      return;
    }
    if(!filtered.some((item)=>item.evidenceId===selectedId)){
      setSelectedId(filtered[0].evidenceId);
    }
  },[filtered,selectedId]);

  const selected=useMemo(
    ()=>data?.items.find((item)=>item.evidenceId===selectedId) || null,
    [data,selectedId]
  );

  useEffect(()=>{
    setReason("");
    setRevision(selected?.narrative || "");
  },[selectedId]);

  async function run(
    key:string,
    action:()=>PromiseLike<{data?:unknown;error?:{message:string}|null}>
  ){
    setBusy(key);
    setError("");
    try{
      const result=await action();
      if(result.error) throw new Error(result.error.message);
      setRefresh((value)=>value+1);
      return result.data;
    }catch(err){
      setError(err instanceof Error ? err.message : "처리 중 오류가 발생했습니다.");
      return null;
    }finally{
      setBusy("");
    }
  }

  if(!data) return <p className="muted">활동 증빙 Inbox를 불러오는 중…</p>;

  return (
    <section className="evidence-workbench-page">
      <div className="section-heading">
        <div>
          <p className="eyebrow">검토함</p>
          <h2>학생이 한 일을 확인하세요</h2>
          <p className="route-description">
            제출된 결과물을 보고 확인하거나 보완을 요청합니다. 평가·성취와 배지 발급 검토도 여기서 이어집니다.
          </p>
        </div>
      </div>

      {error && <p className="error">{error}</p>}

      <CenterPersonalExperienceReview client={client} schoolId={schoolId} />

      <div className="review-stage-tabs">
        <button className="active">1. 결과물 확인</button>
        <button onClick={()=>{window.history.pushState({},"","/evaluation");window.dispatchEvent(new PopStateEvent("popstate"));}}>2. 평가·성취</button>
        <button onClick={()=>{window.history.pushState({},"","/assurance");window.dispatchEvent(new PopStateEvent("popstate"));}}>3. 발급 검토</button>
      </div>

      <div className="workbench-count-grid">
        <CountCard label="검토 대기" value={data.counts.submitted} emphasis />
        <CountCard label="보완 필요" value={data.counts.rejected} />
        <CountCard label="작성 중" value={data.counts.draft} />
        <CountCard label="검증 완료" value={data.counts.verified} good />
      </div>

      <div className="workbench-filter-row">
        {([
          ["SUBMITTED","검토 대기"],
          ["REJECTED","보완 필요"],
          ["DRAFT","작성 중"],
          ["VERIFIED","검증 완료"],
          ["ALL","전체"]
        ] as Array<[StatusFilter,string]>).map(([value,label])=>(
          <button
            key={value}
            className={filter===value ? "filter-chip active" : "filter-chip"}
            onClick={()=>setFilter(value)}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="evidence-workbench-layout">
        <aside className="workbench-list-panel">
          <div className="panel-title">
            <strong>증빙</strong>
            <span>{filtered.length}건</span>
          </div>
          {filtered.map((item)=>(
            <button
              key={item.evidenceId}
              className={selectedId===item.evidenceId ? "workbench-list-row active" : "workbench-list-row"}
              onClick={()=>setSelectedId(item.evidenceId)}
            >
              <strong>{item.title}</strong>
              <span>{item.displayName} · {item.gradeLevel || "-"}학년 {item.classLabel || ""}반</span>
              <small>{statusLabel(item.status)} · {formatDate(item.updatedAt)}</small>
            </button>
          ))}
          {!filtered.length && <p className="case-empty">해당 상태의 증빙이 없습니다.</p>}
        </aside>

        <div className="workbench-detail-panel">
          {!selected ? <p className="case-empty">증빙을 선택하세요.</p> : (
            <>
              <header className="workbench-detail-head">
                <div>
                  <p className="eyebrow">{selected.evidenceType}</p>
                  <h2>{selected.title}</h2>
                  <p>
                    {selected.displayName} · {selected.gradeLevel || "-"}학년 {selected.classLabel || ""}반
                  </p>
                </div>
                <span className={selected.status==="VERIFIED" ? "status-pill good" : "status-pill"}>
                  {statusLabel(selected.status)}
                </span>
              </header>

              <div className="evidence-context-grid">
                <div><span>현재 revision</span><strong>{selected.currentRevision}</strong></div>
                <div><span>레코드 version</span><strong>{selected.version}</strong></div>
                <div><span>관찰일</span><strong>{formatDate(selected.observedAt)}</strong></div>
                <div><span>검증일</span><strong>{formatDate(selected.verifiedAt)}</strong></div>
              </div>

              <section className="workbench-section">
                <h3>근거 내용</h3>
                <div className="evidence-narrative">
                  {selected.narrative || "작성된 서술이 없습니다."}
                </div>
              </section>

              <section className="workbench-section">
                <h3>발생 맥락</h3>
                {selected.source.type==="PARTICIPATION" ? (
                  <div className="source-card">
                    <strong>{selected.source.activityTitle || "활동"}</strong>
                    <span>{selected.source.programTitle || "프로그램"}</span>
                    <small>참여 상태 · {selected.source.participationStatus}</small>
                  </div>
                ) : selected.source.type==="ASSESSMENT_RESULT" ? (
                  <div className="source-card">
                    <strong>검사 파생 결과</strong>
                    <span>{selected.source.assessmentTargetType || "결과"}</span>
                    <small>
                      원점수 {selected.source.rawScore ?? "-"} · 정규화 {selected.source.normalizedScore ?? "-"}
                    </small>
                  </div>
                ) : <p className="muted">별도 발생 맥락이 없습니다.</p>}
              </section>

              {selected.evaluationLinks.length>0 && (
                <section className="workbench-section">
                  <h3>연결된 평가</h3>
                  {selected.evaluationLinks.map((item)=>(
                    <div className="review-line" key={item.evaluationResultId}>
                      <div>
                        <strong>{item.definitionName}</strong>
                        <span>점수 {item.normalizedScore ?? item.numericScore ?? "-"}</span>
                      </div>
                      <span className={item.status==="CONFIRMED" ? "status-pill good" : "status-pill"}>
                        {item.status==="CONFIRMED" ? "확정" : "초안"}
                      </span>
                    </div>
                  ))}
                </section>
              )}

              {selected.status==="SUBMITTED" && (
                <section className="workbench-action-card">
                  <label>
                    검토 메모
                    <textarea
                      value={reason}
                      onChange={(event)=>setReason(event.target.value)}
                      placeholder="검증 또는 보완요청 사유를 기록하세요."
                    />
                  </label>
                  <div className="workbench-action-row">
                    <button
                      className="secondary danger"
                      disabled={busy!==""}
                      onClick={()=>run("reject",()=>client.rpc("center_transition_evidence",{
                        p_school_id:schoolId,
                        p_evidence_id:selected.evidenceId,
                        p_expected_version:selected.version,
                        p_to_status:"REJECTED",
                        p_reason:reason.trim() || "내용 보완이 필요합니다."
                      }))}
                    >
                      보완 요청
                    </button>
                    <button
                      className="primary"
                      disabled={busy!==""}
                      onClick={()=>run("verify",()=>client.rpc("center_transition_evidence",{
                        p_school_id:schoolId,
                        p_evidence_id:selected.evidenceId,
                        p_expected_version:selected.version,
                        p_to_status:"VERIFIED",
                        p_reason:reason.trim() || "증빙 검증 완료"
                      }))}
                    >
                      검증 완료
                    </button>
                  </div>
                </section>
              )}

              {selected.status==="REJECTED" && (
                <section className="workbench-action-card">
                  <label>
                    보완 내용
                    <textarea
                      value={revision}
                      onChange={(event)=>setRevision(event.target.value)}
                      placeholder="보완된 근거 내용을 작성하세요."
                    />
                  </label>
                  <button
                    className="primary"
                    disabled={busy!=="" || !revision.trim()}
                    onClick={()=>run("revise",()=>client.rpc("center_revise_evidence",{
                      p_school_id:schoolId,
                      p_evidence_id:selected.evidenceId,
                      p_expected_version:selected.version,
                      p_narrative:revision.trim()
                    }))}
                  >
                    새 revision 저장
                  </button>
                </section>
              )}

              {selected.status==="DRAFT" && (
                <section className="workbench-action-card">
                  <p>현재 revision을 검토대기 상태로 제출합니다.</p>
                  <button
                    className="primary"
                    disabled={busy!==""}
                    onClick={()=>run("submit",()=>client.rpc("center_transition_evidence",{
                      p_school_id:schoolId,
                      p_evidence_id:selected.evidenceId,
                      p_expected_version:selected.version,
                      p_to_status:"SUBMITTED",
                      p_reason:"Evidence Workbench 제출"
                    }))}
                  >
                    검토 요청
                  </button>
                </section>
              )}
            </>
          )}
        </div>
      </div>
    </section>
  );
}

function CountCard({
  label,value,emphasis=false,good=false
}:{
  label:string;value:number;emphasis?:boolean;good?:boolean;
}){
  const className=good
    ? "workbench-count-card good"
    : emphasis
      ? "workbench-count-card emphasis"
      : "workbench-count-card";

  return <div className={className}><span>{label}</span><strong>{value}</strong></div>;
}
